/**
 * I path della sezione Limulo, in un posto solo — e sotto un prefisso, sempre.
 *
 * ⚠️ PERCHE' NON BASTA «metterle in fondo al router». `createBrowserRouter` (React Router
 * v7) risolve per RANK di specificita', non per ordine di dichiarazione: la posizione
 * nell'array non protegge da niente. E davanti a un path DUPLICATO ESATTO vince chi e'
 * dichiarato prima, **senza errori, senza warning, con la build verde**.
 *
 * Su `main` esistono gia' `tournaments`, `tournaments/:id`, `tournaments/:id/watch-party`
 * e `teams/:id`: sono la superficie tornei del monolite Laravel, viva in produzione. Una
 * rotta nuda di questa sezione le metterebbe in ombra — e siccome le nostre stanno dietro
 * `RoleGate role="beta"`, l'ombra la vedrebbero SOLO gli utenti beta. E' la classe di bug
 * peggiore che si possa costruire qui: rotto per pochi, invisibile a chi lo cerca.
 *
 * La convenzione del repo (chess, duels, chinesepoker la seguono gia') e' il prefisso di
 * sezione. Tenerla qui, in una lista che `tools/verify-limulo.ts` controlla, la rende
 * meccanica invece che ricordata.
 */
export const LIMULO_ROUTE_PREFIX = 'limulo';

export const LIMULO_ROUTE_PATHS = [
  `${LIMULO_ROUTE_PREFIX}/tournaments`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/standings`,
  `${LIMULO_ROUTE_PREFIX}/mine`,
  `${LIMULO_ROUTE_PREFIX}/diagnostics`,
  // ⚠️ In CODA, e non per pigrizia: `LIMULO_PATHS` qui sotto indicizza questo array per
  // POSIZIONE. Inserire in mezzo sposta ogni indice successivo, e il risultato non e' un
  // errore di compilazione — e' una rotta che punta al componente sbagliato, con la build
  // verde. Chi aggiunge, aggiunge in fondo.
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/matches`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/matches/:matchId`,
  `${LIMULO_ROUTE_PREFIX}/profiles`,
  `${LIMULO_ROUTE_PREFIX}/profiles/new/:gameId`,
] as const;

export const LIMULO_PATHS = {
  tournaments: LIMULO_ROUTE_PATHS[0],
  tournamentDetail: LIMULO_ROUTE_PATHS[1],
  standings: LIMULO_ROUTE_PATHS[2],
  mine: LIMULO_ROUTE_PATHS[3],
  diagnostics: LIMULO_ROUTE_PATHS[4],
  matches: LIMULO_ROUTE_PATHS[5],
  matchDetail: LIMULO_ROUTE_PATHS[6],
  profiles: LIMULO_ROUTE_PATHS[7],
  profileCreate: LIMULO_ROUTE_PATHS[8],
} as const;

/**
 * Gli URL di navigazione si costruiscono QUI e non nelle schermate: un `to="/limulo/…"`
 * scritto a mano in un componente e' una copia del path che non si aggiorna quando il
 * path cambia, e un link morto dentro una sezione dietro flag e' l'ultima cosa che
 * qualcuno noterebbe.
 */
export const limuloTournamentUrl = (id: string): string =>
  `/${LIMULO_ROUTE_PREFIX}/tournaments/${encodeURIComponent(id)}`;

export const limuloStandingsUrl = (id: string): string =>
  `${limuloTournamentUrl(id)}/standings`;

export const LIMULO_MINE_URL = `/${LIMULO_ROUTE_PREFIX}/mine`;

/**
 * L'ingresso alla sezione, per chi ci arriva da FUORI (oggi: la scheda Tornei).
 *
 * Sta qui e non nella schermata chiamante per la stessa ragione delle altre: un
 * `to="/limulo/…"` scritto a mano e' un path duplicato che nessun controllo lega a
 * `LIMULO_ROUTE_PATHS`, e il giorno che il prefisso cambia resta indietro in silenzio.
 */
export const LIMULO_TOURNAMENTS_URL = `/${LIMULO_ROUTE_PREFIX}/tournaments`;

/**
 * Gli URL degli incontri.
 *
 * 🔴 L'argomento e' il `tournamentId` (il business id), **mai** `_id`. Tre rotte su cinque
 * rispondono **424** con un ObjectId — costato due PR (#122, e #123 perche' la prima era
 * meta': il link alla classifica aveva lo stesso difetto ed era sopravvissuto alla
 * correzione). Chi chiama passa `tournament.tournamentId || tournament.id`, come fa gia'
 * la scheda torneo.
 */
export const limuloMatchesUrl = (tournamentId: string): string =>
  `${limuloTournamentUrl(tournamentId)}/matches`;

export const limuloMatchUrl = (tournamentId: string, matchId: string): string =>
  `${limuloMatchesUrl(tournamentId)}/${encodeURIComponent(matchId)}`;

/** L'elenco dei propri profili di gioco. Nessun parametro: e' sempre «i miei». */
export const LIMULO_PROFILES_URL = `/${LIMULO_ROUTE_PREFIX}/profiles`;

/**
 * Il modulo per creare il profilo di gioco di UN gioco.
 *
 * ⚠️ Il segmento statico `new` sta PRIMA del parametro: `profiles/new/:gameId` non puo'
 * essere confusa con `profiles`, e non introduce un `:qualcosa` sotto `profiles/` che
 * domani ombreggerebbe una `profiles/:profileId`.
 *
 * `tournament` viaggia in query e non nel path perche' non identifica la schermata: e' il
 * posto DA CUI si e' arrivati, serve solo per tornarci, e un percorso che senza di esso non
 * si potesse aprire renderebbe il modulo irraggiungibile da ogni altro punto.
 * ⚠️ Il `gameId` e' il CODICE del gioco (`tournament.gameId`, passthrough kb#81), mai `_id`.
 */
export const limuloProfileCreateUrl = (gameId: string, tournamentId?: string): string => {
  const base = `/${LIMULO_ROUTE_PREFIX}/profiles/new/${encodeURIComponent(gameId)}`;
  return tournamentId ? `${base}?tournament=${encodeURIComponent(tournamentId)}` : base;
};
